import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material'

interface VhsDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (reason: string) => void | Promise<void>
  onSecondaryConfirm?: () => void | Promise<void>
  title?: string
  warningMessage?: string
  reasonLabel?: string
  initialReason?: string
  confirmButtonText?: string
  secondaryConfirmButtonText?: string
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  isReasonMandatory?: boolean
  showReasonField?: boolean
}

const VhsDialog: React.FC<VhsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onSecondaryConfirm,
  title = 'Confirm',
  warningMessage,
  reasonLabel = 'Reason',
  initialReason = '',
  confirmButtonText = 'Confirm',
  secondaryConfirmButtonText,
  color = 'primary',
  isReasonMandatory = true,
  showReasonField = true,
}) => {
  const [reason, setReason] = useState(initialReason)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (open) {
      setReason(initialReason)
      setError(false)
    }
  }, [open, initialReason])

  const handleConfirm = () => {
    if (
      showReasonField &&
      isReasonMandatory &&
      (!reason || reason.trim() === '')
    ) {
      setError(true)
      return
    }
    onConfirm(showReasonField ? reason.trim() : '')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ color: color === 'error' ? 'error.main' : 'text.primary' }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        {warningMessage && (
          <DialogContentText
            color={color === 'error' ? 'error' : 'textSecondary'}
            sx={{ mb: 2 }}
          >
            {warningMessage}
          </DialogContentText>
        )}
        {showReasonField && (
          <TextField
            autoFocus
            margin="dense"
            label={
              reasonLabel + (isReasonMandatory ? ' (Mandatory)' : ' (Optional)')
            }
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
              if (e.target.value.trim()) setError(false)
            }}
            error={error}
            helperText={
              error && isReasonMandatory ? 'This field is mandatory' : ''
            }
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {onSecondaryConfirm && (
          <Button onClick={onSecondaryConfirm} color="error" variant="outlined">
            {secondaryConfirmButtonText}
          </Button>
        )}
        <Button onClick={handleConfirm} color={color} variant="contained">
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VhsDialog
